import {
  QUERY_RESPONSE_RECEIVED,
  QUERY_REQUIRED,
  QUERY_NOT_REQUIRED,
  QUERY_REQUEST_SENT,
} from '@web-platform/shared-universal-constants/src/network';

import { getKey } from '../getKey';

import type { QueryType } from '@web-platform/shared-universal-types/src/network/requestTypes';
import type { Variables } from '@web-platform/shared-universal-types/src/network/types';
import type { Action } from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type {
  QueryState,
  QueryItem,
} from '@web-platform/shared-universal-types/src/redux/state/network/queries';

export type {
  QueryState,
  QueryItem,
} from '@web-platform/shared-universal-types/src/redux/state/network/queries';

// FIXME: SHIBA-809 Bad typecasting - do not copy this pattern
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
function item<TItem extends QueryItem>(state: TItem = {} as TItem, action: Action): TItem {
  switch (action.type) {
    case QUERY_RESPONSE_RECEIVED: {
      return {
        ...state,
        inFlight: false,
        consecutiveErrorCount: action.error ? state.consecutiveErrorCount + 1 : 0,
        lastResponseAt: action.payload.now,
        lastResponse: action.payload.response,
      };
    }

    case QUERY_REQUEST_SENT: {
      return {
        ...state,
        inFlight: true,
        requestCount: state.requestCount + 1,
        lastRequestAt: action.payload.now,
      };
    }

    case QUERY_REQUIRED: {
      return {
        ...state,
        inFlight: state.inFlight || false,
        consecutiveErrorCount: state.consecutiveErrorCount || 0,
        requestCount: state.requestCount || 0,
        lastResponseAt: state.lastResponseAt || null,
        lastResponse: state.lastResponse || null,
        type: action.payload.type,
        variables: action.payload.variables,
        request: action.payload.request,
        nextRequestAt: action.payload.nextRequestAt
          ? [...(state.nextRequestAt || []), action.payload.nextRequestAt]
          : state.nextRequestAt || [],
        createdAt: action.payload.now,
      };
    }

    case QUERY_NOT_REQUIRED: {
      let removedOne = false;

      return {
        ...state,
        nextRequestAt: state.nextRequestAt.filter((time) => {
          if (time === action.payload.nextRequestAt && !removedOne) {
            removedOne = true;
            return false;
          }

          return true;
        }),
      };
    }

    default:
      return state;
  }
}

export default function (state: QueryState = {}, action: Action): QueryState {
  switch (action.type) {
    case QUERY_RESPONSE_RECEIVED:
    case QUERY_NOT_REQUIRED: {
      const key = getKey(action.payload.type, action.payload.variables);

      // Check if we still have the key/query in state to act on
      if (key in state) {
        return { ...state, [key]: item(state[key], action) };
      }

      return state;
    }

    case QUERY_REQUEST_SENT:
    case QUERY_REQUIRED: {
      const key = getKey(action.payload.type, action.payload.variables);
      return { ...state, [key]: item(state[key], action) };
    }

    default:
      return state;
  }
}

const doesQueryMatchType = <QT extends QueryType>(
  query: QueryItem | undefined,
  type: QT,
): query is QueryItem & { type: QT } => query?.type === type;

export const get = <QT extends QueryType>(
  state: QueryState,
  type: QT,
  variables: Variables,
): (QueryItem & { type: QT }) | undefined => {
  const query = state[getKey(type, variables)];
  return doesQueryMatchType(query, type) ? query : undefined;
};
